import { ICommentDto, IUser } from "@kuika/kuika-cl-model";
import React from "react";
import { createPortal } from "react-dom";
import { getMembers } from "../../../management/board/people-directory";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import { CommentReactions } from "./comment-reactions";
import { formatRelativeTime, highlightMentions, PAGE_SCOPE_KUIKA_ID, renderAvatar } from "./comments-panel-logic";
import { IThreadViewModel, threadKey } from "./comments-panel-types";
import { MentionTextarea } from "./mention-textarea";

// One thread rendered as a card. Purely presentational — every mutation is delegated to the panel via a callback so the data/optimistic logic stays in one place.
interface ICommentThreadCardProps {
  vm: IThreadViewModel;
  pinNumbers: Map<string, number>;
  currentUserId: string | undefined;
  selectedThreadId: string;
  expandedThreadIds: string[];
  editingCommentId: string;
  draftEdit: string;
  replyingThreadId: string;
  draftReply: string;
  openMenuCommentId: string;
  onSelectThread: (key: string, kuikaId: string) => void;
  onHoverThread: (kuikaId: string) => void;
  onToggleExpand: (key: string) => void;
  onOpenMenu: (e: React.MouseEvent, commentId: string) => void;
  onEdit: (comment: ICommentDto) => void;
  onDelete: (commentId: string) => void;
  onEditChange: (value: string) => void;
  onSubmitEdit: () => void;
  onCancelEdit: () => void;
  onStartReply: (key: string) => void;
  onReplyChange: (value: string) => void;
  onSubmitReply: () => void;
  onCancelReply: () => void;
  onResolve: (rootCommentId: string) => void;
  onReopen: (rootCommentId: string) => void;
  onToggleReaction: (commentId: string, emoji: string) => void;
}

export function CommentThreadCard(props: ICommentThreadCardProps): React.ReactElement {
  const { vm, pinNumbers, currentUserId } = props;
  const { root, replies, thread, isResolved, anchorLabel, isDeleted } = vm;

  // Full-screen image preview (lightbox) — opened by clicking an image attachment.
  const [lightboxUrl, setLightboxUrl] = React.useState<string>("");
  React.useEffect((): (() => void) | void => {
    if (!lightboxUrl) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setLightboxUrl("");
    };
    document.addEventListener("keydown", onKey);
    return (): void => document.removeEventListener("keydown", onKey);
  }, [lightboxUrl]);

  const renderMenu = (commentId: string, comment: ICommentDto): React.ReactNode => {
    const isMyComment: boolean = comment.createdUser?.id === currentUserId;
    if (!isMyComment) return null;
    const isOpen: boolean = props.openMenuCommentId === commentId;
    return (
      <div style={{ position: "relative" }}>
        <button
          data-menu-trigger
          className="comments-panel__footer-btn"
          onClick={(e: React.MouseEvent): void => props.onOpenMenu(e, commentId)}
          type="button"
          title="Options"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="12" cy="5" r="1.5" />
            <circle cx="12" cy="12" r="1.5" />
            <circle cx="12" cy="19" r="1.5" />
          </svg>
        </button>
        {isOpen && (
          <div className="comments-panel__menu" onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
            <div className="comments-panel__menu-item" onClick={(): void => props.onEdit(comment)}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
              Edit
            </div>
            <div
              className="comments-panel__menu-item comments-panel__menu-item--danger"
              onClick={(): void => props.onDelete(commentId)}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
              </svg>
              Delete
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAttachment = (comment: ICommentDto): React.ReactNode => {
    if (!comment.attachmentUrl) return null;
    const isImage: boolean = /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(comment.attachmentName ?? "");
    return (
      <div className="comments-panel__attachment" onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
        {isImage ? (
          <img
            src={comment.attachmentUrl}
            alt={comment.attachmentName ?? "attachment"}
            className="comments-panel__attachment-img"
            onClick={(): void => setLightboxUrl(comment.attachmentUrl ?? "")}
            title="Click to enlarge"
          />
        ) : (
          <a href={comment.attachmentUrl} target="_blank" rel="noreferrer" className="comments-panel__attachment-link">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
            </svg>
            {comment.attachmentName ?? "Attachment"}
          </a>
        )}
      </div>
    );
  };

  const renderEditor = (rows: number): React.ReactNode => (
    <div onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
      <MentionTextarea
        className="comments-panel__textarea"
        value={props.draftEdit}
        onChange={props.onEditChange}
        onSubmit={props.onSubmitEdit}
        onCancel={props.onCancelEdit}
        rows={rows}
        autoFocus
      />
      <div style={{ display: "flex", gap: 6, marginTop: 4, justifyContent: "flex-end" }}>
        <KuikaButton type="text" size="sm" onClick={props.onCancelEdit}>
          Cancel
        </KuikaButton>
        <KuikaButton type="primary" size="sm" onClick={props.onSubmitEdit}>
          Save
        </KuikaButton>
      </div>
    </div>
  );

  const resolvedBy: IUser | undefined = root?.resolvedByUser;
  const resolvedByName: string = resolvedBy
    ? `${resolvedBy.firstName ?? ""} ${resolvedBy.lastName ?? ""}`.trim() || (resolvedBy.username ?? "")
    : "";
  const resolvedAtText: string = root?.resolvedAt ? formatRelativeTime(root.resolvedAt) : "";
  const resolvedTitle: string = resolvedByName
    ? `Resolved by ${resolvedByName}${resolvedAtText ? ` · ${resolvedAtText}` : ""}`
    : "Resolved";
  const key: string = threadKey(thread);
  const isSelected: boolean = props.selectedThreadId === key;
  const isExpanded: boolean = props.expandedThreadIds.includes(key);
  const isPageThread: boolean = thread.kuikaId === PAGE_SCOPE_KUIKA_ID;
  const displayOrder: number | undefined = pinNumbers.get(key);

  const cardClass: string = [
    "comments-panel__card",
    isSelected ? "comments-panel__card--selected" : "",
    isResolved ? "comments-panel__card--resolved" : ""
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      key={key}
      className={cardClass}
      onClick={(): void => props.onSelectThread(key, thread.kuikaId)}
      onMouseEnter={(): void => props.onHoverThread(thread.kuikaId)}
      data-testid={`comment-thread-${key}`}
    >
      {/* Card header */}
      <div className="comments-panel__card-header">
        <div
          className={`comments-panel__pin-badge${isResolved ? " comments-panel__pin-badge--resolved" : ""}${
            isPageThread ? " comments-panel__pin-badge--page" : ""
          }`}
          title={isPageThread ? "Page-level comment" : undefined}
        >
          {isPageThread ? (
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
              <path d="M14 3v6h6" />
            </svg>
          ) : (
            (displayOrder ?? thread.order)
          )}
        </div>
        {root &&
          renderAvatar(
            root.createdUser?.id ?? "x",
            root.createdUser?.firstName,
            root.createdUser?.lastName,
            root.createdUser?.username
          )}
        <div className="comments-panel__card-meta">
          <div className="comments-panel__card-top">
            <span className="comments-panel__author">
              {root
                ? `${root.createdUser?.firstName ?? ""} ${root.createdUser?.lastName ?? ""}`.trim() ||
                  root.createdUser?.username
                : ""}
            </span>
            {root && <span className="comments-panel__time">{formatRelativeTime(root.createdAt)}</span>}
          </div>
          {isResolved && (
            <div className="comments-panel__resolved-line">
              <span className="comments-panel__resolved-badge" title={resolvedTitle}>
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span className="comments-panel__resolved-text">
                  Resolved{resolvedByName ? ` · ${resolvedByName}` : ""}
                </span>
              </span>
            </div>
          )}
          <div className="comments-panel__anchor-chip">
            {isDeleted ? (
              // The component is gone, but we kept the label it had at comment time — show it
              // (e.g. "Button · deleted") so the thread still says WHAT it was about.
              <span className="comments-panel__anchor-deleted">
                {anchorLabel ? `${anchorLabel} · deleted` : "Component deleted"}
              </span>
            ) : (
              anchorLabel
            )}
          </div>
        </div>
        {root && renderMenu(root.id, root)}
      </div>

      {/* Root comment body */}
      {root && (
        <>
          {props.editingCommentId === root.id ? (
            renderEditor(3)
          ) : (
            <div className="comments-panel__body">
              {highlightMentions(root.comment, getMembers())}
              {root.isEdited && <span className="comments-panel__edited-mark"> (edited)</span>}
            </div>
          )}
          {renderAttachment(root)}
          <CommentReactions comment={root} currentUserId={currentUserId} onToggleReaction={props.onToggleReaction} />
        </>
      )}

      {/* Footer */}
      <div className="comments-panel__card-footer">
        {replies.length > 0 && (
          <span
            className="comments-panel__reply-toggle"
            onClick={(e: React.MouseEvent): void => {
              e.stopPropagation();
              props.onToggleExpand(key);
            }}
          >
            {isExpanded ? "▲" : "▼"} {replies.length} replies
          </span>
        )}
        <button
          className="comments-panel__footer-btn"
          type="button"
          onClick={(e: React.MouseEvent): void => {
            e.stopPropagation();
            props.onStartReply(key);
          }}
        >
          Reply
        </button>
        {/* Resolve stays available even when the anchored component is gone, so an orphaned thread
            can be closed out (with attribution) instead of only deletable. */}
        {root &&
          (isResolved ? (
            <button
              className="comments-panel__footer-btn comments-panel__footer-btn--primary"
              type="button"
              data-testid="commentReopenButton"
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                props.onReopen(root.id);
              }}
            >
              Reopen
            </button>
          ) : (
            <button
              className="comments-panel__footer-btn"
              type="button"
              data-testid="commentResolveButton"
              onClick={(e: React.MouseEvent): void => {
                e.stopPropagation();
                props.onResolve(root.id);
              }}
            >
              Resolve
            </button>
          ))}
      </div>

      {/* Replies */}
      {isExpanded && replies.length > 0 && (
        <div className="comments-panel__replies">
          {replies.map((reply: ICommentDto) => (
            <div key={reply.id} className="comments-panel__reply-item">
              <div className="comments-panel__reply-header">
                {renderAvatar(
                  reply.createdUser?.id ?? "x",
                  reply.createdUser?.firstName,
                  reply.createdUser?.lastName,
                  reply.createdUser?.username,
                  "sm"
                )}
                <span className="comments-panel__reply-author">
                  {`${reply.createdUser?.firstName ?? ""} ${reply.createdUser?.lastName ?? ""}`.trim() ||
                    reply.createdUser?.username}
                </span>
                <span className="comments-panel__reply-time">{formatRelativeTime(reply.createdAt)}</span>
                {renderMenu(reply.id, reply)}
              </div>
              {props.editingCommentId === reply.id ? (
                renderEditor(2)
              ) : (
                <div className="comments-panel__reply-body">
                  {highlightMentions(reply.comment, getMembers())}
                  {reply.isEdited && <span className="comments-panel__edited-mark"> (edited)</span>}
                </div>
              )}
              {renderAttachment(reply)}
              <CommentReactions
                comment={reply}
                currentUserId={currentUserId}
                onToggleReaction={props.onToggleReaction}
              />
            </div>
          ))}
        </div>
      )}

      {/* Inline reply composer */}
      {props.replyingThreadId === key && (
        <div className="comments-panel__reply-composer" onClick={(e: React.MouseEvent): void => e.stopPropagation()}>
          <div className="comments-panel__reply-input-wrap">
            <MentionTextarea
              className="comments-panel__textarea"
              value={props.draftReply}
              onChange={props.onReplyChange}
              onSubmit={props.onSubmitReply}
              onCancel={props.onCancelReply}
              placeholder="Write a reply... (@ to mention)"
              rows={2}
              autoFocus
            />
            <div style={{ display: "flex", gap: 6, marginTop: 4, justifyContent: "flex-end" }}>
              <KuikaButton type="text" size="sm" onClick={props.onCancelReply}>
                Cancel
              </KuikaButton>
              <KuikaButton type="primary" size="sm" onClick={props.onSubmitReply}>
                Send
              </KuikaButton>
            </div>
          </div>
        </div>
      )}

      {lightboxUrl &&
        createPortal(
          <div className="comments-panel__lightbox" onClick={(): void => setLightboxUrl("")}>
            <button
              className="comments-panel__lightbox-close"
              type="button"
              aria-label="Close"
              onClick={(): void => setLightboxUrl("")}
            >
              ×
            </button>
            <img
              src={lightboxUrl}
              alt="attachment preview"
              className="comments-panel__lightbox-img"
              onClick={(e: React.MouseEvent): void => e.stopPropagation()}
            />
          </div>,
          document.body
        )}
    </div>
  );
}
